import { AnimatePresence, motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import {
  Plus,
  Search,
  Pin,
  Trash2,
  Clock,
  MessageSquare,
  BookOpen,
  Settings,
  MoreHorizontal,
  Download,
  Share2,
  FileText,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { Thread, ThreadCategory, ChatSettings } from "@/types/ai-chat";
import { Logo } from "@/components/common/Logo";

const CATEGORY_META: Record<
  ThreadCategory | "all",
  { label: string; color: string }
> = {
  all: { label: "All", color: "var(--color-charcoal)" },
  "kra-registration": { label: "KRA Reg.", color: "#6B1A2A" },
  "tax-returns": { label: "Tax Returns", color: "#1D4ED8" },
  etims: { label: "eTIMS", color: "#0F6E56" },
  bookkeeping: { label: "Bookkeeping", color: "#7C3AED" },
  advisory: { label: "Advisory", color: "#D97706" },
  audit: { label: "Audit", color: "#DC2626" },
  international: { label: "International", color: "#0369A1" },
  training: { label: "Training", color: "#16A34A" },
  general: { label: "General", color: "#6B7280" },
};

const AI_MODELS = [
  {
    id: "rosewood-expert-v2",
    name: "Rosewood Expert",
    description: "Best for comprehensive tax guidance",
    badge: "Default",
  },
  {
    id: "rosewood-fast",
    name: "Rosewood Fast",
    description: "Faster responses for quick questions",
    badge: "Faster",
  },
  {
    id: "rosewood-research",
    name: "Rosewood Research",
    description: "Deep analysis with full citations",
    badge: "Deep",
  },
] as const;

const RESOURCES = [
  { title: "eTIMS Quick Start Guide", type: "PDF", size: "2.4 MB" },
  { title: "Kenya Tax Calendar 2024", type: "PDF", size: "1.1 MB" },
  { title: "PAYE Tax Tables 2024", type: "XLSX", size: "854 KB" },
  { title: "VAT Guide for Businesses", type: "PDF", size: "3.8 MB" },
  { title: "Bookkeeping Template", type: "XLSX", size: "1.2 MB" },
];

function formatRelativeDate(date: Date) {
  const diff = Math.floor(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  return new Intl.DateTimeFormat("en-KE", {
    month: "short",
    day: "numeric",
  }).format(date);
}

function CategoryBadge({ category }: { category: ThreadCategory }) {
  const meta = CATEGORY_META[category];
  return (
    <span
      className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
      style={{ background: `${meta.color}18`, color: meta.color }}
    >
      {meta.label}
    </span>
  );
}

interface ThreadItemProps {
  thread: Thread;
  isActive: boolean;
  onClick: () => void;
  onPin: (id: string) => void;
  onDelete: (id: string) => void;
}

function ThreadItem({
  thread,
  isActive,
  onClick,
  onPin,
  onDelete,
}: ThreadItemProps) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -6 }}
      className={cn(
        "group relative flex flex-col gap-1 px-3 py-2.5 rounded-[var(--radius-md)] cursor-pointer transition-all duration-150 select-none",
        isActive
          ? "bg-[#FAF0F2] border border-[var(--color-burgundy)]/20"
          : "hover:bg-[var(--color-surface)] border border-transparent",
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {thread.pinned && (
            <Pin
              size={10}
              className="text-[var(--color-gold)] flex-shrink-0 fill-[var(--color-gold)]"
            />
          )}
          <p
            className={cn(
              "text-[12.5px] font-semibold leading-snug truncate",
              isActive
                ? "text-[var(--color-burgundy)]"
                : "text-[var(--color-charcoal)]",
            )}
          >
            {thread.title}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] ">
            {formatRelativeDate(thread.updatedAt)}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
                aria-label="Thread options"
              >
                <MoreHorizontal size={11} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              <DropdownMenuItem onClick={() => onPin(thread.id)}>
                <Pin size={12} className="mr-2" />
                {thread.pinned ? "Unpin" : "Pin"}
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Share2 size={12} className="mr-2" />
                Share thread
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Download size={12} className="mr-2" />
                Export
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500"
                onClick={() => onDelete(thread.id)}
              >
                <Trash2 size={12} className="mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <p className="text-[11px]  leading-snug line-clamp-1">{thread.preview}</p>

      <div className="flex items-center gap-2">
        <CategoryBadge category={thread.category} />
        <span className="text-[10px] ">{thread.messageCount} msgs</span>
      </div>
    </motion.div>
  );
}

interface ChatSidebarProps {
  threads: Thread[];
  activeThreadId: string | null;
  threadSearch: string;
  threadFilter: ThreadCategory | "all";
  settings: ChatSettings;
  isOpen: boolean;
  onToggle: () => void;
  onNewChat: () => void;
  onSelectThread: (id: string) => void;
  onPinThread: (id: string) => void;
  onDeleteThread: (id: string) => void;
  onSearchChange: (q: string) => void;
  onFilterChange: (f: ThreadCategory | "all") => void;
  onSettingsChange: (patch: Partial<ChatSettings>) => void;
}

export function ChatSidebar({
  threads,
  activeThreadId,
  threadSearch,
  threadFilter,
  settings,
  isOpen,
  onNewChat,
  onSelectThread,
  onPinThread,
  onDeleteThread,
  onSearchChange,
  onFilterChange,
  onSettingsChange,
}: ChatSidebarProps) {
  if (!isOpen) return null;

  const pinned = threads.filter((t) => t.pinned);
  const recent = threads.filter((t) => !t.pinned);

  return (
    <motion.aside
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 280, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="flex flex-col h-full bg-white border-r border-[var(--color-border)] overflow-hidden flex-shrink-0"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
      
      <Logo />

      </div>

      <div className="px-3 py-2.5">
        <Button
          className="w-full gradient-burgundy text-white text-[12.5px] font-semibold gap-2 hover:opacity-90"
          onClick={onNewChat}
        >
          <Plus size={14} />
          New Conversation
        </Button>
      </div>

      <Tabs defaultValue="threads" className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-3 mb-0 h-8 grid grid-cols-3 bg-[var(--color-surface)] rounded-[var(--radius-md)]">
          <TabsTrigger
            value="threads"
            className="text-[11px] gap-1 data-[state=active]:bg-white data-[state=active]:text-[var(--color-burgundy)]"
          >
            <MessageSquare size={11} />
            Chats
          </TabsTrigger>
          <TabsTrigger
            value="resources"
            className="text-[11px] gap-1 data-[state=active]:bg-white data-[state=active]:text-[var(--color-burgundy)]"
          >
            <BookOpen size={11} />
            Docs
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            className="text-[11px] gap-1 data-[state=active]:bg-white data-[state=active]:text-[var(--color-burgundy)]"
          >
            <Settings size={11} />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="threads" className="flex-1 min-h-0 mt-0">
          <div className="px-3 pt-2.5 pb-1 space-y-2">
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 "
              />
              <Input
                value={threadSearch}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search conversations…"
                className="pl-7 h-8 text-[12px] bg-[var(--color-surface)] border-[var(--color-border)] focus-visible:ring-[var(--color-burgundy)]/20"
              />
            </div>

            <div className="flex flex-wrap gap-1">
              {(
                ["all", "etims", "audit", "tax-returns", "bookkeeping"] as const
              ).map((f) => (
                <button
                  key={f}
                  onClick={() => onFilterChange(f)}
                  className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all",
                    threadFilter === f
                      ? "bg-[var(--color-burgundy)] text-white"
                      : "bg-[var(--color-surface)]  hover:bg-[var(--color-border)]",
                  )}
                >
                  {CATEGORY_META[f].label}
                </button>
              ))}
            </div>
          </div>

          <ScrollArea className="h-full px-3 pb-4">
            {pinned.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <Pin
                    size={10}
                    className="text-[var(--color-gold)] fill-[var(--color-gold)]"
                  />
                  <span className="text-[10px] font-semibold  uppercase tracking-wider">
                    Pinned
                  </span>
                </div>
                <div className="space-y-0.5">
                  <AnimatePresence mode="popLayout">
                    {pinned.map((t) => (
                      <ThreadItem
                        key={t.id}
                        thread={t}
                        isActive={t.id === activeThreadId}
                        onClick={() => onSelectThread(t.id)}
                        onPin={onPinThread}
                        onDelete={onDeleteThread}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {recent.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <Clock size={10} className="" />
                  <span className="text-[10px] font-semibold  uppercase tracking-wider">
                    Recent
                  </span>
                </div>
                <div className="space-y-0.5">
                  <AnimatePresence mode="popLayout">
                    {recent.map((t) => (
                      <ThreadItem
                        key={t.id}
                        thread={t}
                        isActive={t.id === activeThreadId}
                        onClick={() => onSelectThread(t.id)}
                        onPin={onPinThread}
                        onDelete={onDeleteThread}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {threads.length === 0 && (
              <div className="text-center py-10">
                <MessageSquare
                  size={22}
                  className="text-[var(--color-border)] mx-auto mb-2"
                />
                <p className="text-[12px] ">No conversations yet</p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="resources" className="flex-1 min-h-0 mt-0">
          <ScrollArea className="h-full px-3 py-3">
            <p className="text-[10px] font-semibold  uppercase tracking-wider px-1 mb-3">
              Tax Resources
            </p>

            <div className="space-y-1.5">
              {RESOURCES.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 p-2.5 rounded-[var(--radius-md)] hover:bg-[var(--color-surface)] cursor-pointer group transition-colors border border-transparent hover:border-[var(--color-border)]"
                >
                  <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[#FAF0F2] flex items-center justify-center flex-shrink-0">
                    <FileText
                      size={14}
                      className="text-[var(--color-burgundy)]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11.5px] font-semibold text-[var(--color-charcoal)] truncate">
                      {r.title}
                    </p>
                    <p className="text-[10px] ">
                      {r.type} · {r.size}
                    </p>
                  </div>
                  <Download
                    size={12}
                    className=" opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                </div>
              ))}
            </div>

            <Link
              to="/resources"
              className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-[11.5px] font-semibold text-[var(--color-burgundy)] hover:bg-[#FAF0F2] rounded-[var(--radius-md)] transition-colors"
            >
              View all resources <ArrowUpRight size={11} />
            </Link>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="settings" className="flex-1 min-h-0 mt-0">
          <ScrollArea className="h-full px-3 py-3">
            <div className="space-y-5">
              <div>
                <p className="text-[10px] font-semibold  uppercase tracking-wider mb-2 px-1">
                  AI Model
                </p>
                <div className="space-y-1.5">
                  {AI_MODELS.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => onSettingsChange({ model: model.id })}
                      className={cn(
                        "w-full flex items-start gap-2.5 p-2.5 rounded-[var(--radius-md)] border transition-all text-left",
                        settings.model === model.id
                          ? "border-[var(--color-burgundy)] bg-[#FAF0F2]"
                          : "border-[var(--color-border)] hover:border-[var(--color-burgundy)]/40 bg-white",
                      )}
                    >
                      <div
                        className={cn(
                          "w-3.5 h-3.5 rounded-full border-2 mt-0.5 flex-shrink-0 transition-all",
                          settings.model === model.id
                            ? "border-[var(--color-burgundy)] bg-[var(--color-burgundy)]"
                            : "border-[var(--color-border)]",
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-[12px] font-semibold text-[var(--color-charcoal)]">
                            {model.name}
                          </p>
                          <Badge
                            variant="outline"
                            className="text-[9px] h-4 px-1.5 font-bold border-[var(--color-gold)]/40 bg-[#FAF5E4] text-[#8B6914]"
                          >
                            {model.badge}
                          </Badge>
                        </div>
                        <p className="text-[10.5px] ">{model.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-[10px] font-semibold  uppercase tracking-wider mb-3 px-1">
                  Features
                </p>
                <div className="space-y-3">
                  {(
                    [
                      {
                        key: "streamingEnabled" as const,
                        label: "Live streaming",
                        desc: "See responses as they type",
                      },
                      {
                        key: "citationsEnabled" as const,
                        label: "Citations",
                        desc: "Include law references",
                      },
                      {
                        key: "memoryEnabled" as const,
                        label: "Conversation memory",
                        desc: "Remember context across chats",
                      },
                    ] as const
                  ).map(({ key, label, desc }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-3"
                    >
                      <div>
                        <Label className="text-[12px] font-medium text-[var(--color-charcoal)] cursor-pointer">
                          {label}
                        </Label>
                        <p className="text-[10px] ">{desc}</p>
                      </div>
                      <Switch
                        checked={settings[key]}
                        onCheckedChange={(v) => onSettingsChange({ [key]: v })}
                        className="data-[state=checked]:bg-[var(--color-burgundy)]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </motion.aside>
  );
}
